import {
    ENTRY_JOB_REJECTION_MESSAGES,
    EntryJobRejectionReason,
    IEntryJobValidationErrors,
    IEntryJobValidationMessages,
} from '../models/entry.interface';

const ENTRY_JOBS_KEY = 'entry_jobs';
const FALLBACK_MESSAGE = 'Entry details are invalid';

function isBranch(node: unknown): node is IEntryJobValidationErrors {
    return typeof node === 'object' && node !== null && !Array.isArray(node);
}

/**
 * The message for one rejection reason.
 *
 * A reason without a message is a reason the API has since gained, so it is
 * logged rather than only worded generically -- the generic wording alone
 * would leave nothing to tell us a message is missing.
 */
function messageFor(reason: string): string {
    const message = ENTRY_JOB_REJECTION_MESSAGES[reason as EntryJobRejectionReason];
    if (!message) {
        console.error('Unrecognised entry job rejection reason', reason);
        return FALLBACK_MESSAGE;
    }
    return message;
}

/** Every message under a node, whatever depth the failing field sits at. */
function messagesFrom(node: unknown): string[] {
    if (Array.isArray(node)) {
        return node.map(reason => messageFor(String(reason)));
    }
    if (!isBranch(node)) {
        console.error('Unrecognised entry job validation error', node);
        return [FALLBACK_MESSAGE];
    }
    return Object.values(node).flatMap(child => messagesFrom(child));
}

/**
 * Read the API's schema validation errors as messages to show.
 *
 * The API reports a validation failure as the schema's own error object rather
 * than as wording, so the reasons are turned into sentences here. Errors under
 * `entry_jobs` are keyed by the index of the entry that caused them, unless
 * `entry_jobs` failed as a whole -- an empty or oversized list is about the
 * request, and no entry is to blame for it.
 */
export function entryJobValidationMessages(body: unknown): IEntryJobValidationMessages {
    const request: string[] = [];
    const entries = new Map<number, string[]>();
    if (!isBranch(body)) {
        return { request, entries };
    }
    Object.entries(body).forEach(([field, node]) => {
        if (field !== ENTRY_JOBS_KEY || !isBranch(node)) {
            request.push(...messagesFrom(node));
            return;
        }
        Object.entries(node).forEach(([index, entryNode]) => {
            const position = Number(index);
            if (!Number.isInteger(position)) {
                request.push(...messagesFrom(entryNode));
                return;
            }
            entries.set(position, messagesFrom(entryNode));
        });
    });
    return { request, entries };
}
